import {
  acceptTransferMatch,
  DEFAULT_WINDOW_DAYS,
  requestTransferMatches,
} from '#banksync/transfer-match';

export type TransferMatchProfile = {
  id: string;
  name: string;
  payeeIds: string[];
  windowDays: number;
  accountIds: string[];
  enabled: boolean;
};

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((x): x is string => typeof x === 'string')
    : [];
}

// defensive parse | the synced pref blob may be absent, malformed, or written by
// a newer/older client, so guard every field and drop unusable entries.
export function parseProfiles(
  raw: string | null | undefined,
): TransferMatchProfile[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out: TransferMatchProfile[] = [];
  for (const p of parsed) {
    if (!p || typeof p !== 'object') continue;
    const o = p as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.name !== 'string') continue;
    out.push({
      id: o.id,
      name: o.name,
      payeeIds: stringArray(o.payeeIds),
      windowDays:
        typeof o.windowDays === 'number'
          ? Math.max(0, Math.min(14, o.windowDays))
          : DEFAULT_WINDOW_DAYS,
      accountIds: stringArray(o.accountIds),
      enabled: o.enabled !== false,
    });
  }
  return out;
}

export function serializeProfiles(profiles: TransferMatchProfile[]): string {
  return JSON.stringify(profiles);
}

export function emptyProfile(id: string): TransferMatchProfile {
  return {
    id,
    name: '',
    payeeIds: [],
    windowDays: DEFAULT_WINDOW_DAYS,
    accountIds: [],
    enabled: true,
  };
}

// the matcher + link calls are injectable so the runner can be unit-tested
// without mocking the #banksync/transfer-match module (which is brittle across
// platforms). production callers use the defaults.
export type RunProfilesDeps = {
  requestTransferMatches: typeof requestTransferMatches;
  acceptTransferMatch: typeof acceptTransferMatch;
};

// run every enabled, payee-scoped profile | silently link the unambiguous
// matches it finds. a profile with no payee scope never auto-links (safety).
export async function runTransferProfiles(
  profiles: TransferMatchProfile[],
  deps: RunProfilesDeps = { requestTransferMatches, acceptTransferMatch },
): Promise<{ linked: number }> {
  let linked = 0;
  for (const profile of profiles) {
    if (!profile.enabled || profile.payeeIds.length === 0) continue;
    try {
      const { matches } = await deps.requestTransferMatches({
        windowDays: profile.windowDays,
        payeeIds: profile.payeeIds,
        accountIds: profile.accountIds,
        unambiguousOnly: true,
        // auto-link only stable, cleared transactions | freshly-imported bank
        // pending are excluded here so they cannot be silently mis-paired. They
        // still surface in the manual review modal, which omits this flag.
        onlyCleared: true,
      });
      for (const match of matches) {
        try {
          await deps.acceptTransferMatch(match);
          linked += 1;
        } catch (err) {
          // one bad pair must not suppress the rest | keep the count honest so
          // the user is still told about the links that did go through
          console.error('Transfer profile link failed:', err);
        }
      }
    } catch (err) {
      // a failing profile must not abort the others
      console.error('Transfer profile run failed:', err);
    }
  }
  return { linked };
}
